import { useRef, useEffect } from "react";
import { Task } from "../types/Task";
import { MediaOrderedDetails } from "../types/TaskDetailTypes";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";

/**
 * Reason values for media maintenance tasks
 */
const REASONS = {
  MISSING: "missing",
  DAMAGED: "damaged",
  NEW_PTAGS: "newPTags",
  REMOVE: "remove",
} as const;

/**
 * Check if MediasQuantity field should be visible for the given reason
 */
function shouldShowQuantityField(reason: string | null): boolean {
  if (!reason) return false;
  return reason === REASONS.MISSING || reason === REASONS.DAMAGED;
}

/**
 * Check if MediasPrintPtags field should be visible for the given reason
 */
function shouldShowPtagsField(reason: string | null): boolean {
  if (!reason) return false;
  return (
    reason === REASONS.MISSING ||
    reason === REASONS.DAMAGED ||
    reason === REASONS.NEW_PTAGS
  );
}

/**
 * Hook to reset media ordered details when reason changes
 *
 * This hook watches the task.details.reason field and automatically resets
 * pieces_ordered and ptags_ordered to their default values when the reason
 * changes.
 *
 * @param task - The current task object
 * @param onTaskChange - Callback to update the task
 * @param mediasFieldPath - Path to the medias array in task.details (default: "details.medias")
 */
export function useMediaResetOnReasonChange(
  task: Record<string, unknown>,
  onTaskChange: (updates: Partial<Task>) => void,
  mediasFieldPath: string = "details.medias"
): void {
  // Track the previous reason value to detect changes
  const prevReasonRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const currentReason = getByPath(task, "details.reason") as
      | { value: string }
      | null
      | undefined;
    const currentReasonValue = currentReason?.value ?? null;

    // On first mount, just store the current reason without resetting
    if (prevReasonRef.current === undefined) {
      prevReasonRef.current = currentReasonValue;
      return;
    }

    // If reason hasn't changed, do nothing
    if (prevReasonRef.current === currentReasonValue) {
      return;
    }

    // Reason has changed - reset media ordered details
    const medias = getByPath(task, mediasFieldPath) as
      | MediaOrderedDetails[]
      | null
      | undefined;

    if (Array.isArray(medias) && medias.length > 0) {
      const isQuantityVisible = shouldShowQuantityField(currentReasonValue);
      const isPtagsVisible = shouldShowPtagsField(currentReasonValue);

      const resetMedias = medias.map((media) => ({
        ...media,
        pieces_ordered: {
          quantity: isQuantityVisible ? media.pieces : 0,
          completed: false,
        },
        ptags_ordered: {
          quantity: isPtagsVisible ? 1 : 0,
          completed: false,
        },
      }));

      // Update only the configured medias field to avoid broad details merges.
      onTaskChange(setByPath(mediasFieldPath, resetMedias) as Partial<Task>);
    }

    // Update the ref to the new reason
    prevReasonRef.current = currentReasonValue;
  }, [task, onTaskChange, mediasFieldPath]);
}
